import { Button, ButtonVariant } from '@/components'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { logEvent } from '@/logging'
import { chargeNowEventCollection } from '@/reducers/chargeNowEvents'
import {
  chargeForecastCollection,
  chargesCollection,
} from '@/reducers/chargeRelatedCollections'
import { selectNextDeviceDREvent } from '@/selectors/vehicleDemandResponseEvent/selectNextVehicleDREvent'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { useState } from 'react'
import DROptOutModal from './SmartChargeSchedule/DROptOutModal'
import VehicleDREventOptInConfirmationModal from '@/app/features/vehicleDemandResponseEvent/VehicleDREventOptInConfirmationModal'
import DeviceOptedOutModal from '@/app/features/vehicleDemandResponseEvent/DeviceOptedOutModal'
import { vehicleDemandResponseEventsCollection } from '@/reducers/vehicleDemandResponseEvents'
import { VEHICLE_DR_EVENT_HELP_URL } from '@/app/features/vehicleDemandResponseEvent/drEventHelpUrls'
import { ChargeForecast } from '@/types'

type ChargeButtonViewModelProps = {
  vehicleChargerId?: number
  vehicleId?: number
  deviceType: 'vehicle' | 'vehicle_charger'
}

function useViewModel({
  vehicleChargerId,
  deviceType,
  vehicleId,
}: ChargeButtonViewModelProps) {
  const id = vehicleChargerId ?? vehicleId
  const { data } = chargeForecastCollection.useFetch({
    params: {
      charger_id: vehicleChargerId,
      vehicle_id: vehicleId,
    },
  })
  const chargeForecast = data.find(
    (item) => item.vehicle_id === id || item.charger_id === id,
  )

  const { refetch: refetchCharges } = chargesCollection.useFetch({
    params: {
      charger_id: vehicleChargerId,
      vehicle_id: vehicleId,
    },
  })

  const { data: chargeNowEvents, refetch: refetchChargeNowEvents } =
    chargeNowEventCollection.useFetch(`${deviceType}/${id}/charge_now`)

  const chargeNowEventStatus = chargeForecast?.charge_now_status
  const dispatch = useAppDispatch()

  // Check for DR events
  const deviceId = vehicleChargerId ?? vehicleId
  const deviceTypeForSelector: DeviceType =
    deviceType === 'vehicle_charger' ? 'vehicleCharger' : 'vehicle'
  const nextDREvent = deviceId
    ? useAppSelector((state) =>
        selectNextDeviceDREvent(state, deviceId, deviceTypeForSelector),
      )
    : null

  const [isDRManageEventModalOpen, setIsDRManageEventModalOpen] =
    useState(false)
  const [isOptedInModalOpen, setIsOptedInModalOpen] = useState(false)
  const [isOptedOutModalOpen, setIsOptedOutModalOpen] = useState(false)

  const startChargeNow = () => {
    dispatch(
      chargeNowEventCollection.actions.create(
        {},
        {
          pathSegments: [`${deviceType}/${id}/charge_now`],
        },
      ),
    ).then(async () => {
      await dispatch(chargeForecastCollection.actions.invalidate())
      refetchChargeNowEvents()
      refetchCharges()
      dispatch(vehicleDemandResponseEventsCollection.actions.invalidate())
    })
  }

  const performStopChargeNow = () => {
    const chargeNowEvent = chargeNowEvents?.[0]

    if (!chargeNowEvent) {
      return
    }

    dispatch(
      chargeNowEventCollection.actions.update(
        `${deviceType}/${id}/charge_now/${chargeNowEvent.id}/`,
        { complete: true },
      ),
    ).then(async () => {
      await dispatch(chargeForecastCollection.actions.invalidate())
      refetchChargeNowEvents()
      refetchCharges()
      dispatch(vehicleDemandResponseEventsCollection.actions.invalidate())
    })
  }

  const stopChargeNow = () => {
    if (nextDREvent && nextDREvent.enrolled === false) {
      setIsOptedInModalOpen(true)
      return
    }

    performStopChargeNow()
  }

  const startChargeNowAfterOptOut = () => {
    if (chargeNowEventStatus === 'startable') {
      logEvent('chargeforecast_chargenow_start')
      startChargeNow()
    } else if (chargeNowEventStatus === 'boostable') {
      logEvent('chargeforecast_chargenow_boost')
      startChargeNow()
    }
  }

  const chargeNowButtonAction = () => {
    if (
      (chargeNowEventStatus === 'startable' ||
        chargeNowEventStatus === 'boostable') &&
      nextDREvent &&
      nextDREvent.enrolled === true
    ) {
      setIsDRManageEventModalOpen(true)
    } else if (chargeNowEventStatus === 'startable') {
      logEvent('chargeforecast_chargenow_start')
      startChargeNow()
    } else if (chargeNowEventStatus === 'boostable') {
      logEvent('chargeforecast_chargenow_boost')
      startChargeNow()
    } else if (chargeNowEventStatus === 'stoppable') {
      logEvent('chargeforecast_chargenow_stop')
      stopChargeNow()
    }
  }

  const handleDROptOut = () => {
    setIsDRManageEventModalOpen(false)
    setIsOptedOutModalOpen(true)
    startChargeNowAfterOptOut()
  }

  const handleOptedIn = () => {
    setIsOptedInModalOpen(false)
    performStopChargeNow()
  }

  let buttonText = ''
  let buttonVariant: ButtonVariant = 'primary'
  if (chargeNowEventStatus === 'startable') {
    buttonText = 'Start Charging'
    buttonVariant = 'primary-subtle'
  } else if (chargeNowEventStatus === 'boostable') {
    buttonText = 'Charge Non-stop'
    buttonVariant = 'primary-subtle'
  } else if (chargeNowEventStatus === 'stoppable') {
    buttonText = 'Return to Smart Charging'
  }

  const show = ['startable', 'boostable', 'stoppable'].includes(
    chargeNowEventStatus ?? '',
  )

  return {
    chargeNowButtonAction,
    buttonText,
    show,
    buttonVariant,
    isDRManageEventModalOpen,
    setIsDRManageEventModalOpen,
    isOptedInModalOpen,
    setIsOptedInModalOpen,
    isOptedOutModalOpen,
    setIsOptedOutModalOpen,
    nextDREvent,
    handleDROptOut,
    handleOptedIn,
    deviceTypeForSelector,
    deviceId,
    chargeNowEventStatus,
  }
}

type ChargeNowButtonProps = ChargeButtonViewModelProps & {
  // Charge Forecast v2 uses the subtle style for every state and labels both
  // startable and boostable charges "Charge Non-stop"
  appearance?: 'default' | 'v2'
  className?: string
}

export const V2_BUTTON_TEXT: Partial<
  Record<ChargeForecast['charge_now_status'], string>
> = {
  startable: 'Charge Non-stop',
  boostable: 'Charge Non-stop',
  stoppable: 'Return to Smart Charging',
}

export function ChargeNowButton({
  appearance = 'default',
  className,
  ...props
}: ChargeNowButtonProps) {
  const {
    chargeNowButtonAction,
    buttonText,
    show,
    buttonVariant,
    isDRManageEventModalOpen,
    setIsDRManageEventModalOpen,
    isOptedInModalOpen,
    setIsOptedInModalOpen,
    isOptedOutModalOpen,
    setIsOptedOutModalOpen,
    nextDREvent,
    handleDROptOut,
    handleOptedIn,
    deviceTypeForSelector,
    deviceId,
    chargeNowEventStatus,
  } = useViewModel(props)
  const isV2 = appearance === 'v2'

  if (!show) {
    return null
  }

  return (
    <div>
      <Button
        id="charge-forecast-charge-now-button"
        variant={isV2 ? 'primary-subtle' : buttonVariant}
        onClick={chargeNowButtonAction}
        className={className}
      >
        {(isV2 &&
          chargeNowEventStatus &&
          V2_BUTTON_TEXT[chargeNowEventStatus]) ||
          buttonText}
      </Button>
      {nextDREvent && (
        <DROptOutModal
          open={isDRManageEventModalOpen}
          onClose={() => setIsDRManageEventModalOpen(false)}
          drEvent={nextDREvent}
          deviceType={deviceTypeForSelector}
          onOptedOut={handleDROptOut}
          skipOptOutApiCall={true}
          helpLinkUrl={
            deviceTypeForSelector === 'vehicle'
              ? VEHICLE_DR_EVENT_HELP_URL
              : null
          }
        />
      )}
      <VehicleDREventOptInConfirmationModal
        open={isOptedInModalOpen}
        onClose={handleOptedIn}
        deviceId={deviceId ?? 0}
        deviceType={deviceTypeForSelector}
      />
      <DeviceOptedOutModal
        open={isOptedOutModalOpen}
        onClose={() => setIsOptedOutModalOpen(false)}
      />
    </div>
  )
}
